import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { catchError, map, Observable, of, take, tap } from 'rxjs';
import { appStatusMap, Status } from '../constants/AppStatuses';
import { Employee } from '../models/generated/employee';
import { PhysicianResponse } from '../models/InterfaceResponses';
import { convertPhysician } from '../utils/buildTJ-v2';
import { CustomHttpError } from '../utils/CustomHttpError';
import { EnvironmentService } from './environment.service';
import { StatusService } from './status.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class PhysicianService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly env = inject(EnvironmentService);
  private readonly toastService = inject(ToastService);
  private readonly statusService = inject(StatusService);

  public physicianMap: WritableSignal<{ [key: string]: Employee }> = signal({});
  public list: Signal<Array<Employee>> = computed(() => Object.values(this.physicianMap() || {}));

  public getPhysicians(): Observable<Array<Employee>> {
    return this.http.get<Array<PhysicianResponse>>(`${this.env.getBaseRestUrl()}/physician`).pipe(
      take(1),
      map((physicians: Array<PhysicianResponse>) => {
        return physicians
          .map((physicianResponse) => convertPhysician(physicianResponse))
          .sort((a, b) => a.lastName.localeCompare(b.lastName));
      }),
      tap((physicians: Array<Employee>) => {
        if (!physicians?.length) {
          this.statusService.setStatus(appStatusMap[Status.NOT_POSSIBLE_TO_CREATE_NO_DATA]);
        } else {
          this.statusService.removeStatus(Status.NOT_POSSIBLE_TO_CREATE_NO_DATA);
        }

        this.physicianMap.set(
          physicians.reduce((map, physician) => {
            map[physician.id] = physician;
            return map;
          }, {})
        );
      }),
      catchError((e: CustomHttpError) => {
        console.error('Error requesting physicians', e);
        this.toastService.genericError(e.traceId);
        return of([]);
      })
    );
  }

  public physicianExists(value: string | Employee): Observable<boolean> {
    if (!value) {
      return of(false);
    }

    const id = typeof value === 'string' ? value : value.id;

    return of(!!this.physicianMap()?.[id]);
  }
}
